<script setup lang="ts">
import BaseModal from "@/views/Auth/components/BaseModal.vue";
import { useI18n } from "vue-i18n";
import TextInput from "@/components/inputs/TextInput.vue";
import { computed, ref, defineProps, defineEmits, watch } from "vue";
import GooglePlaceAutocompleteInput from "@/components/inputs/GooglePlaceAutocompleteInput.vue";
import AppDropdown from "@/components/inputs/AppDropdown.vue";
import { FamilyMember, Gender, GENDERS } from "@/types/familyTypes";
import ChevronIcon from "@/components/icons/ChevronIcon.vue";
import DatePicker from "@/components/inputs/DatePicker.vue";
import { PlaceSelectedPayload } from "@/types/google-places";
import { useFamilyStore } from "@/stores/familyStore";
import { useToastNotificationStore } from "@/stores/toastNotificationStore";
import { useGoogleAutocomplete } from "@/utils/useGoogleAutocomplete";

const props = defineProps<{
  show: boolean;
  editableFamilyMember: FamilyMember | undefined;
}>();

const emit = defineEmits(["close"]);

watch(
  () => props.editableFamilyMember,
  (newValue: FamilyMember | undefined) => {
    if (newValue === undefined) {
      name.value = "";
      gender.value = "male";
      birthday.value = null;
      location.value = undefined;
      timezone.value = "";
      cityName.value = "";
      return;
    }

    name.value = newValue.name;
    gender.value = newValue.gender;
    birthday.value = new Date(newValue.birthdate);
    location.value = { lat: newValue.latitude, lng: newValue.longitude };
    timezone.value = newValue.timezone;
    cityName.value = newValue.city;
  }
);

const { t } = useI18n();
const { normalizeGoogleTimeZone } = useGoogleAutocomplete("ru");
const { addFamilyMember, updateFamilyMember } = useFamilyStore();
const toastStore = useToastNotificationStore();

const name = ref<string>("");
const gender = ref<Gender>("male");
const birthday = ref<Date | null>(null);
const location = ref<{ lat: number; lng: number }>();
const timezone = ref<string>("");
const cityName = ref<string>("");

const isGenderDropdownOpen = ref(false);
const waitApiCall = ref(false);
const isFormValid = computed(() => {
  return (
    name.value.length > 0 &&
    location.value !== undefined &&
    birthday.value !== null &&
    cityName.value.length > 0 &&
    timezone.value.length > 0
  );
});

function handleSelectGender(newGender: Gender) {
  gender.value = newGender;
  isGenderDropdownOpen.value = false;
}

function handlePlaceSelected(payload: PlaceSelectedPayload) {
  location.value = {
    lat: payload.location.lat(),
    lng: payload.location.lng(),
  };
  cityName.value = payload.locationName;
  timezone.value = normalizeGoogleTimeZone(payload.timeZone);
}

async function handleSubmit() {
  if (waitApiCall.value || !isFormValid.value) {
    return;
  }
  try {
    waitApiCall.value = true;
    const request = {
      name: name.value,
      city: cityName.value,
      latitude: location.value?.lat as number,
      longitude: location.value?.lng as number,
      timezone: timezone.value,
      gender: gender.value,
      birthdate: birthday.value as Date,
    };
    if (props.editableFamilyMember !== undefined) {
      await updateFamilyMember(request, props.editableFamilyMember);
    } else {
      await addFamilyMember(request);
    }
    emit("close");
  } catch (e) {
    await toastStore.createNotificationsFromApi(e);
    console.log(e);
  } finally {
    waitApiCall.value = false;
  }
}
</script>

<template>
  <BaseModal
    :show="show"
    :maxHeight="'504px'"
    :maxWidth="'480px'"
    @close="emit('close')"
  >
    <div class="flex flex-col select-none">
      <p class="font-semibold text-2xl mb-10">
        {{
          editableFamilyMember === undefined
            ? t("family-table.add-title")
            : t("family-table.update-title")
        }}
      </p>
      <TextInput
        :label="t('family-table.member.name')"
        v-model="name"
        type="text"
        class="mb-4"
      />
      <div class="w-full mb-4">
        <p class="text-left mb-2 text-brown text-base leading-[20px] sf-pro">
          {{ t("family-table.member.city") }}
        </p>
        <GooglePlaceAutocompleteInput
          styleType="outline"
          :initialQuery="editableFamilyMember?.city ?? undefined"
          @placeSelected="handlePlaceSelected"
        />
      </div>
      <div class="flex gap-4 mb-10">
        <!-- GENDER SELECTOR -->
        <AppDropdown
          borderColor="border-muted"
          labelWidthClass="font-normal"
          :label="t('family-table.member.gender')"
          :items="GENDERS"
          :selectedItem="gender"
          :isDropdownOpen="isGenderDropdownOpen"
          @toggle="isGenderDropdownOpen = !isGenderDropdownOpen"
        >
          <template #selectedItem="{ item }">
            <span class="flex justify-between items-center px-4 text-base">
              <span>{{ t(`family-table.gender.${item}`) }}</span>
              <ChevronIcon
                class="transition-transform"
                :class="isGenderDropdownOpen ? 'rotate-180' : 'rotate-0'"
              />
            </span>
          </template>
          <template #dropdownItem="{ item }">
            <div
              class="flex items-center px-4 text-base h-10 hover:bg-divider cursor-pointer select-none"
              @click="handleSelectGender(item)"
            >
              <p>{{ t(`family-table.gender.${item}`) }}</p>
            </div>
          </template>
        </AppDropdown>
        <!-- BIRTH DATE INPUT -->
        <div class="w-full">
          <p
            class="text-brown text-base leading-[20px] select-none mb-2 sf-pro"
          >
            {{ t("family-table.member.birthdate") }}
          </p>
          <DatePicker
            :showDatePickIcon="false"
            :initialDate="editableFamilyMember?.birthdate ?? undefined"
            styleType="outline"
            @dateSelected="(newDate: Date) => (birthday = newDate)"
          />
        </div>
      </div>
      <button
        class="bg-orange-gradient py-4 rounded-full"
        :class="{
          'opacity-40 pointer-events-none': waitApiCall || !isFormValid,
        }"
        @click="handleSubmit"
      >
        <span class="text-white font-semibold text-xl leading-[24px]">
          {{
            editableFamilyMember === undefined
              ? t("buttons.add")
              : t("buttons.save")
          }}
        </span>
      </button>
    </div>
  </BaseModal>
</template>
